import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bed, Users, RefreshCw, AlertCircle, CheckCircle2, Clock, Minus, Activity } from 'lucide-react';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

const BED_STATUS = {
  available:      { label: 'Available',      color: 'bg-green-50 border-green-200',   dot: 'bg-clinical-success' },
  occupied:       { label: 'Occupied',        color: 'bg-blue-50 border-blue-200',     dot: 'bg-blue-500' },
  under_cleaning: { label: 'Cleaning',        color: 'bg-yellow-50 border-yellow-200', dot: 'bg-yellow-500' },
  reserved:       { label: 'Reserved',        color: 'bg-purple-50 border-purple-200', dot: 'bg-purple-500' },
  inactive:       { label: 'Inactive',        color: 'bg-gray-50 border-gray-200',     dot: 'bg-gray-300' },
};

function BedCard({ bed, onStatusChange }: any) {
  const navigate = useNavigate();
  const s = BED_STATUS[bed.status as keyof typeof BED_STATUS] ?? BED_STATUS.inactive;
  return (
    <div className={clsx('border rounded-xl p-3 transition-all hover:shadow-md', s.color)}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-navy text-sm">{bed.bed_number}</span>
        <span className={clsx('w-2.5 h-2.5 rounded-full', s.dot)} />
      </div>

      {bed.patient ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-700 truncate">{bed.patient.patient_uid}</p>
          <p className="text-xs text-gray-500 truncate">{bed.patient.name}</p>
          <p className="text-xs text-gray-400">{new Date(bed.patient.admission_date).toLocaleDateString('en-IN')}</p>
          {bed.patient.admission_id && (
            <button
              onClick={() => navigate(`/ipd/vitals/${bed.patient.admission_id}`)}
              className="mt-1.5 w-full flex items-center justify-center gap-1 text-xs bg-blue-100 text-blue-700 rounded-lg py-1 hover:bg-blue-200 transition"
            >
              <Activity size={10} /> Vitals Chart
            </button>
          )}
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic">{s.label}</p>
      )}

      {bed.status === 'under_cleaning' && (
        <button
          onClick={() => onStatusChange(bed.id, 'available')}
          className="mt-2 w-full text-xs bg-green-100 text-green-700 rounded-lg py-1 hover:bg-green-200 transition"
        >
          Mark Available
        </button>
      )}
    </div>
  );
}

function StatBadge({ label, value, color }: any) {
  return (
    <div className={clsx('rounded-xl border p-4 shadow-card', color)}>
      <div className="text-2xl font-bold text-navy">{value ?? 0}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

export default function IpdBedBoardPage() {
  const queryClient = useQueryClient();
  const [selectedWard, setSelectedWard] = useState<string>('all');

  const { data, isLoading, refetch } = useQuery({
    queryKey:        ['ipd-beds'],
    queryFn:         () => api.get('/ipd/beds').then(r => r.data),
    refetchInterval: 60_000,
  });

  const { data: census } = useQuery({
    queryKey:  ['ipd-census'],
    queryFn:   () => api.get('/ipd/census').then(r => r.data),
    staleTime: 30_000,
  });

  const patchBed = useMutation({
    mutationFn: ({ id, status }: any) => api.patch(`/ipd/beds/${id}`, { status }),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['ipd-beds'] }),
  });

  const wards = data?.wards ?? [];
  const stats = data?.stats ?? {};

  const filteredWards = selectedWard === 'all'
    ? wards
    : wards.filter((w: any) => w.id === selectedWard);

  return (
    <div className="space-y-6 p-6 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">IPD Bed Board</h1>
          <p className="text-sm text-gray-400 mt-0.5">Real-time ward occupancy</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''}/>
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatBadge label="Total Beds"      value={stats.total}         color="bg-white border-gray-200"/>
        <StatBadge label="Occupied"        value={stats.occupied}      color="bg-blue-50 border-blue-200"/>
        <StatBadge label="Available"       value={stats.available}     color="bg-green-50 border-green-200"/>
        <StatBadge label="Under Cleaning"  value={stats.under_cleaning} color="bg-yellow-50 border-yellow-200"/>
      </div>

      {/* Today's census */}
      {census && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-card flex gap-6 text-sm">
          <div><span className="text-gray-400">Admitted today:</span> <span className="font-semibold text-navy">{census.today?.admissions ?? 0}</span></div>
          <div><span className="text-gray-400">Discharged today:</span> <span className="font-semibold text-navy">{census.today?.discharges ?? 0}</span></div>
          <div><span className="text-gray-400">Occupancy:</span> <span className="font-semibold text-navy">{stats.total ? Math.round((stats.occupied / stats.total) * 100) : 0}%</span></div>
        </div>
      )}

      {/* Ward filter */}
      {wards.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedWard('all')}
            className={clsx('px-3 py-1.5 rounded-lg text-sm border transition',
              selectedWard === 'all'
                ? 'bg-navy text-white border-navy'
                : 'bg-white text-gray-600 border-gray-200 hover:border-navy'
            )}
          >
            All Wards
          </button>
          {wards.map((w: any) => (
            <button
              key={w.id}
              onClick={() => setSelectedWard(w.id)}
              className={clsx('px-3 py-1.5 rounded-lg text-sm border transition',
                selectedWard === w.id
                  ? 'bg-navy text-white border-navy'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-navy'
              )}
            >
              {w.name}
            </button>
          ))}
        </div>
      )}

      {/* Bed grid by ward */}
      {isLoading ? (
        <div className="text-center py-16 text-gray-400">Loading bed board…</div>
      ) : filteredWards.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Bed size={40} className="mx-auto mb-3 opacity-30"/>
          <p>No wards configured. Add wards from hospital settings.</p>
        </div>
      ) : (
        filteredWards.map((ward: any) => (
          <div key={ward.id} className="bg-white border border-gray-200 rounded-xl shadow-card overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-navy">{ward.name}</h3>
                <p className="text-xs text-gray-400">{ward.type?.replace('_', ' ').toUpperCase()} · Floor {ward.floor ?? '—'}</p>
              </div>
              <div className="text-sm text-gray-500">
                {ward.beds.filter((b: any) => b.status === 'occupied').length}/{ward.beds.length} occupied
              </div>
            </div>
            <div className="p-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {ward.beds.map((bed: any) => (
                <BedCard
                  key={bed.id}
                  bed={bed}
                  onStatusChange={(id: string, status: string) => patchBed.mutate({ id, status })}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
